import type { Settings, Page } from 'types';
import type { BlockReviews } from 'types/block-reviews';
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import type { QueryError } from '../api';
import { AllConditionsResult, getAllConditions } from './conditions';
import type { StateLawsListResult } from './state-law';
import type { VpdPage } from 'types/acf';
import type { JobsByTeamResult } from './jobs';
import type { LocationItems } from './locations';
import type { PartnersReviewsResult, ReviewsListResult } from './reviews';
import type { GetDashboardInitialData } from './dashboard';
import type { AlphabetStrain, GetInitialStrainsData } from './strains';
import jwt from 'jsonwebtoken';
import camelcaseKeys from 'camelcase-keys';
import { initializeWpApollo } from 'lib/apollo';
import { request } from 'lib/rest';
import { getStateLawsList } from './state-law';
import { getLanguageCodeFilter, getSettings } from './settings';
import { getPartnersLocations } from './partners';
import { getJobsByTeam } from './jobs';
import { getPartnersReviews } from './reviews';
import { getDashboardInitialData } from './dashboard';
import { getAllStrains, getInitialStrainsData } from './strains';

export type AllStatePagesResult = {
  slug: string[];
  locale: string;
}[];

export async function getAllStatePages(): Promise<AllStatePagesResult> {
  try {
    const { error, data } = await request<{ slug: string; locale: string }[]>(
      `/state-pages`,
    );

    if (error) {
      throw { error };
    }

    // make slug an array and filter the empty strings
    // e.g. '/medical-card/ohio' -> ['medical-card', 'ohio']
    return data.items.map(({ slug, locale }) => {
      return {
        slug: slug.split('/').filter(Boolean),
        locale,
      };
    });
  } catch (error) {
    console.log(`error getting all state pages:`, JSON.stringify(error));

    throw error;
  }
}

export async function getPageData({
  slug,
  locale,
  preview,
}: {
  slug: string;
  locale: string;
  preview?: boolean;
}): Promise<Page> {
  const fields =
    'id,title,date,modified,slug,link,content,seo,acf,translations,language,type';
  const { error, data } = await request<Page[]>(
    `/pages?path=${slug}&_fields=${fields}${preview ? '&status=draft' : ''}`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type FrontPage = {
  page: Page;
  settings: Settings;
  partnersReviews: PartnersReviewsResult['partnersReviews'];
  isPreview: boolean;
};

export async function getFrontPage({
  locale,
  preview,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<FrontPage>> {
  try {
    const [homepage, settings] = await Promise.all([
      getPageData({ locale, slug: 'homepage', preview }),
      getSettings(locale),
    ]);

    if (!homepage) {
      return {
        notFound: true,
      };
    }

    if (!settings) {
      return {
        notFound: true,
      };
    }

    let partnersReviewsData = null;

    const reviewsBlock = homepage.acf.blocks?.find(
      ({ acfFcLayout }) => acfFcLayout == 'reviews',
    ) as BlockReviews;
    const isTrustPilotWidget = reviewsBlock ? reviewsBlock.trustpilot : false;

    if (reviewsBlock && !isTrustPilotWidget) {
      const client = initializeWpApollo();
      const { error: partnersReviewsError, partnersReviews } =
        await getPartnersReviews({ client });

      if (partnersReviewsError) {
        throw partnersReviewsError;
      }

      partnersReviewsData = partnersReviews;
    }

    return {
      props: {
        settings: {
          ...settings,
          pageTranslations: homepage.translations,
        },
        page: homepage,
        partnersReviews: partnersReviewsData,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting home:`, error);

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

export type PageProps = {
  page: Page;
  settings: Settings;
  conditions?: AllConditionsResult;
  dashboard?: GetDashboardInitialData;
  lawStatuses?: StateLawsListResult;
  locations?: LocationItems;
  reviews?: ReviewsListResult;
  partnersReviews?: PartnersReviewsResult['partnersReviews'];
  strains?: GetInitialStrainsData;
  strainsList?: AlphabetStrain[];
  teams?: JobsByTeamResult['teams'];
  vpdEvents?: VpdPage;
  variant?: 'test' | 'control';
  siteTitle?: string;
  isPreview: boolean;
};

export async function getPage({
  locale,
  params,
  preview,
  variant = 'control',
}: GetStaticPropsContext & { variant?: 'control' | 'test' }): Promise<
  GetStaticPropsResult<PageProps>
> {
  const slug = Array.isArray(params.slug) ? params.slug.join('/') : params.slug;

  try {
    const languageCode = getLanguageCodeFilter(locale);

    const [page, settings] = await Promise.all([
      getPageData({ locale, slug, preview }),
      getSettings(locale),
    ]);

    // Since our partners use urls like `/partner-name` we need a way to check if the current url
    // is indeed a partner or an ad, fetch its data (`/partner/partner-name` || `/ad/partner-name`) and redirect to the correct
    // medical card page with the state defined or with the user's state
    if (!page && settings && params.slug?.length === 1) {
      const { error: partnerError, data: partnerData } = await request(
        `/partner?slug=${params.slug[0]}`,
      );
      const { error: adError, data: adData } = await request(
        `/ad?slug=${params.slug[0]}`,
      );

      if (partnerError) {
        throw { partnerError };
      }

      if (adError) {
        throw { adError };
      }

      let state = null;
      let adState = null;
      const { statePages } = settings;

      const isPartner = partnerData?.totalItems > 0;
      const isAd = adData?.totalItems > 0;

      let showAd = !isPartner && isAd ? true : false;

      if (partnerData && isPartner) {
        const { states } = partnerData.items[0].acf;
        // get the state slug using state code
        if (states?.length) {
          state = statePages.find(item => item.code === states[0])?.slug;
        }
      }

      if (adData && isAd) {
        const { states } = adData.items[0].acf;
        // get the state slug using state code
        if (states?.length) {
          adState = statePages.find(item => item.code === states[0])?.slug;
        }

        // partners redirects have priority so we must check that if a partner exists the state must match
        if (adState == state && isPartner) {
          showAd = true;
        }
      }

      if (isPartner && !showAd) {
        // redirect to `medical-card` with or without state and the `utm_source` as the partner name
        return {
          redirect: {
            permanent: true,
            destination: `/medical-card${state ? '/' + state : ''}?utm_source=${
              params.slug
            }`,
          },
        };
      }
      if (isPartner && showAd) {
        // redirect to `medical-card` with or without state and the `utm_source` & `utm_campaign` as the partner name
        return {
          redirect: {
            permanent: true,
            destination: `/medical-card${state ? '/' + state : ''}?utm_source=${
              params.slug
            }&utm_campaign=${params.slug}`,
          },
        };
      }
      if (!isPartner && showAd) {
        // redirect to `medical-card` with or without state and the `utm_campaign` as the partner name
        return {
          redirect: {
            permanent: true,
            destination: `/medical-card${
              adState ? '/' + adState : ''
            }?utm_campaign=${params.slug}`,
          },
        };
      }
    }

    if (!page || !settings) {
      return {
        notFound: true,
      };
    }

    const pageLanguage = page.language;

    // redirect locale page to correct URL
    // ex: /slug-in-spanish -> /es/slug-in-spanish
    if (!preview && pageLanguage !== locale) {
      return page.slug
        ? {
            redirect: {
              permanent: true,
              destination: page.link,
            },
          }
        : {
            notFound: true,
          };
    }

    const pageType = page.acf.pageType;
    const reviewsBlock =
      page.acf.blocks &&
      (page.acf.blocks.find(
        ({ acfFcLayout }) => acfFcLayout == 'reviews',
      ) as BlockReviews);
    const isTrustPilotWidget = reviewsBlock ? reviewsBlock.trustpilot : false;

    let pageData: PageProps = {
      page,
      settings: {
        ...settings,
        pageTranslations: page.translations,
      },
      isPreview: !!preview,
    };

    if (reviewsBlock && !isTrustPilotWidget) {
      const client = initializeWpApollo();
      const { error: partnersReviewsError, partnersReviews } =
        await getPartnersReviews({ client });

      if (partnersReviewsError) {
        throw partnersReviewsError;
      }
      pageData = { ...pageData, partnersReviews: partnersReviews };
    }

    if (pageType === 'careers') {
      const { teams } = await getJobsByTeam();

      pageData = { ...pageData, teams };
    }

    if (pageType === 'partners_map') {
      const client = initializeWpApollo();
      const { error: partnerLocationsError, locations } =
        await getPartnersLocations({
          client,
          languageCode,
        });

      if (partnerLocationsError) {
        throw partnerLocationsError;
      }

      pageData = {
        ...pageData,
        locations,
      };
    }

    if (pageType === 'dashboard') {
      const client = initializeWpApollo();
      const dashboard = await getDashboardInitialData({
        client,
        languageCode,
        locale,
      });

      if ((dashboard as QueryError)?.error) {
        throw (dashboard as QueryError)?.error;
      }

      pageData = {
        ...pageData,
        dashboard: dashboard as GetDashboardInitialData,
      };
    }

    if (pageType === 'strains_archive') {
      const client = initializeWpApollo();
      const strains = await getInitialStrainsData({
        client,
        languageCode,
      });

      if ((strains as QueryError)?.error) {
        throw (strains as QueryError)?.error;
      }

      pageData = { ...pageData, strains: strains as GetInitialStrainsData };
    }

    if (pageType === 'conditions') {
      const conditions = await getAllConditions(locale);

      pageData = { ...pageData, conditions: conditions as any };
    }

    if (slug === 'cannabis-strains' || slug === 'cepas-de-cannabis') {
      const strainsList = await getAllStrains({ locale });

      pageData = { ...pageData, strainsList };
    }

    if (pageType === 'laws') {
      const lawStatuses = await getStateLawsList(locale);
      pageData = { ...pageData, lawStatuses };
    }

    if (pageType === 'affiliate_landing') {
      pageData = {
        ...pageData,
        vpdEvents: pageData?.page?.acf?.vpdPage,
      };
    }

    return {
      props: {
        ...pageData,
        variant,
      },
    };
  } catch (error) {
    console.log(`error getting page ${slug}:`, JSON.stringify(error));

    throw error;
  }
}

export function getResearchIframeUrl(): string | null {
  let token = '';

  try {
    token = jwt.sign(
      {
        resource: { dashboard: 51 },
        params: {},
      },
      process.env.METABASE_SECRET_KEY,
    );

    return `${process.env.METABASE_SITE_URL}/embed/dashboard/${token}#bordered=false&titled=false`;
  } catch (error) {
    console.log(error);

    return null;
  }
}
